/**
 * DataTable — generic paginated + sortable + filterable table.
 *
 * The first three pages we built (audit, workflows, devices) each rolled
 * their own filter+pagination logic on top of TableShell. The site dashboard
 * has two paginated tables on the same page; that's the third use, time to
 * extract the abstraction.
 *
 * Usage:
 *   <DataTable
 *     columns={[
 *       { key: "name",   label: "Name",   render: (r) => r.name, sortable: true },
 *       { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
 *     ]}
 *     data={rows}
 *     searchPlaceholder="Filter…"
 *     searchableFields={["name", "status"]}
 *     pageSize={10}
 *   />
 *
 * Features:
 *   - free-text search across `searchableFields` (case-insensitive substring)
 *   - click a column header to sort (sortable: true)
 *   - paginated with prev/next/first/last buttons + page-size selector
 *   - extra `toolbar` slot for per-table filter chips
 *   - optional total override so callers can show the server count when
 *     fixture vs live diverge
 */
import { useMemo, useReducer, useState, type ReactNode } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface DataTableColumn<T> {
  /** Key used for sort + as React key. */
  key: string;
  /** Header label. */
  label: ReactNode;
  /** Cell renderer. */
  render: (row: T) => ReactNode;
  /** Optional explicit width (CSS value); defaults to 1fr. */
  width?: string;
  /** When true, the column header is clickable to sort by `sortValue`. */
  sortable?: boolean;
  /** Returns the raw sort key for a row. Defaults to render() output. */
  sortValue?: (row: T) => string | number;
  /** Right-align the cell — used for numeric columns. */
  align?: "left" | "right";
  /** Optional export column title. Defaults to key. */
  exportLabel?: string;
  /** Optional export value accessor. Defaults to sortValue then raw field value. */
  exportValue?: (row: T) => string | number | boolean | null | undefined;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  /** Stable id per row for React keys. */
  rowKey: (row: T) => string;

  /** Free-text search input — set to undefined to hide it. */
  searchPlaceholder?: string;
  /** Which keys to search across when the user types. */
  searchableFields?: Array<keyof T>;
  /** Custom search predicate; takes precedence over `searchableFields`. */
  searchPredicate?: (row: T, query: string) => boolean;

  /** Default page size; user can override via the selector. */
  pageSize?: number;
  /** Page size options. Default: [10, 25, 50, 100]. */
  pageSizeOptions?: number[];
  /** Optional initial search text for deep-linked entry points. */
  initialQuery?: string;

  /** Optional override for the headline total (e.g. server-side total). */
  totalOverride?: number;
  /** Suffix appended to the total (e.g. "shown · 1293 online of 1299 (last hour)"). */
  totalSuffix?: ReactNode;
  /** Show the default right-side total in the toolbar row. */
  showTotal?: boolean;
  /** Right-side header content — filter chips, action buttons. */
  toolbar?: ReactNode;
  /** Empty-state message when there are no rows post-filter. */
  emptyMessage?: ReactNode;
  /** Enable CSV/XLSX export buttons for currently filtered rows. */
  enableExport?: boolean;
  /** Base filename used by export actions. */
  exportFileName?: string;

  className?: string;
}

type SortDirection = "asc" | "desc" | null;
type ExportRow = Record<string, string | number | boolean | null>;

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

function buildExportRows<T>(rows: T[], columns: DataTableColumn<T>[]): ExportRow[] {
  return rows.map((row) => {
    const out: ExportRow = {};
    for (const column of columns) {
      const key = column.exportLabel ?? column.key;
      if (column.exportValue) {
        out[key] = column.exportValue(row) ?? "";
        continue;
      }
      if (column.sortValue) {
        out[key] = column.sortValue(row);
        continue;
      }
      const raw = (row as Record<string, unknown>)[column.key];
      out[key] =
        raw === null || raw === undefined
          ? ""
          : typeof raw === "object"
            ? JSON.stringify(raw)
            : (raw as string | number | boolean);
    }
    return out;
  });
}

function exportCsvRows(rows: ExportRow[], exportFileName: string): void {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escapeCell = (value: unknown) => {
    const text = String(value ?? "");
    const escaped = text.replace(/"/g, '""');
    return /[",\n]/.test(text) ? `"${escaped}"` : escaped;
  };
  const lines = [
    headers.map(escapeCell).join(","),
    ...rows.map((row) => headers.map((h) => escapeCell(row[h])).join(",")),
  ];
  downloadBlob(
    new Blob([`${lines.join("\n")}\n`], { type: "text/csv;charset=utf-8;" }),
    `${exportFileName}.csv`,
  );
}

async function exportXlsxRows(
  rows: ExportRow[],
  exportFileName: string,
  setExporting: (v: boolean) => void,
): Promise<void> {
  if (rows.length === 0) return;
  setExporting(true);
  try {
    const XLSX = await import("xlsx");
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    const bytes = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    downloadBlob(
      new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${exportFileName}.xlsx`,
    );
  } finally {
    setExporting(false);
  }
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  searchPlaceholder,
  searchableFields,
  searchPredicate,
  pageSize: initialPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  initialQuery = "",
  totalOverride,
  totalSuffix,
  showTotal = true,
  toolbar,
  emptyMessage = "No rows match your filters.",
  enableExport = false,
  exportFileName = "table-data",
  className,
}: DataTableProps<T>) {
  const animatedRowsRef = useUiAutoAnimate();

  type TableState = { query: string; pageSize: number; page: number; sortKey: string | null; sortDir: SortDirection };
  const [{ query, pageSize, page, sortKey, sortDir }, setTable] = useReducer(
    (s: TableState, p: Partial<TableState>) => ({ ...s, ...p }),
    { query: initialQuery, pageSize: initialPageSize, page: 1, sortKey: null, sortDir: null },
  );
  const [exportingXlsx, setExportingXlsx] = useState(false);

  // Apply search → sort → paginate.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    if (searchPredicate) {
      return data.filter((r) => searchPredicate(r, q));
    }
    if (searchableFields && searchableFields.length > 0) {
      return data.filter((r) =>
        searchableFields.some((f) =>
          String((r as Record<string, unknown>)[f as string] ?? "")
            .toLowerCase()
            .includes(q),
        ),
      );
    }
    // Fallback: search across every cell's rendered string.
    return data.filter((r) =>
      columns.some((c) => {
        const v = c.sortValue ? c.sortValue(r) : c.render(r);
        return String(v ?? "")
          .toLowerCase()
          .includes(q);
      }),
    );
  }, [data, query, searchPredicate, searchableFields, columns]);

  const sorted = useMemo(() => {
    if (!sortKey || !sortDir) return filtered;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return filtered;
    const valueOf =
      col.sortValue ?? ((row: T) => String(col.render(row) ?? ""));
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va === vb) return 0;
      const cmp = va < vb ? -1 : 1;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir, columns]);

  const total = totalOverride ?? filtered.length;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const visible = sorted.slice(start, start + pageSize);

  function toggleSort(key: string) {
    if (sortKey !== key) {
      setTable({ sortKey: key, sortDir: "asc", page: 1 });
    } else if (sortDir === "asc") {
      setTable({ sortDir: "desc", page: 1 });
    } else if (sortDir === "desc") {
      setTable({ sortKey: null, sortDir: null, page: 1 });
    }
  }

  const colTemplate = columns.map((c) => c.width ?? "1fr").join(" ");

  const exportRows = useMemo(
    () => buildExportRows(sorted, columns),
    [sorted, columns],
  );

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-line bg-surface",
        className,
      )}
    >
      {/* Toolbar */}
      {searchPlaceholder !== undefined ||
      toolbar ||
      totalSuffix ||
      enableExport ? (
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          {searchPlaceholder !== undefined ? (
            <div className="relative w-full max-w-sm">
              <Search
                size={14}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
              />
              <Input
                value={query}
                onChange={(e) => {
                  setTable({ query: e.target.value, page: 1 });
                }}
                placeholder={searchPlaceholder}
                className="pl-9"
              />
            </div>
          ) : null}
          {toolbar}
          <div className="ml-auto flex items-center gap-2">
            {enableExport ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={exportRows.length === 0}
                  >
                    <Download size={12} className="text-fg-dim" />
                    Download
                    <ChevronDown size={11} className="text-fg-mute" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => exportCsvRows(exportRows, exportFileName)}>
                    <FileText size={12} className="mr-1 text-fg-dim" />
                    CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => void exportXlsxRows(exportRows, exportFileName, setExportingXlsx)}
                    disabled={exportingXlsx}
                  >
                    <FileSpreadsheet size={12} className="mr-1 text-fg-dim" />
                    {exportingXlsx ? "Generating XLSX…" : "XLSX"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {showTotal ? (
              <div className="font-mono text-[11px] text-fg-mute">
                {total} {totalSuffix}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Header row */}
      <div
        className="grid items-center gap-3 border-b border-line bg-surface-tint/40 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-fg-mute"
        style={{ gridTemplateColumns: colTemplate }}
      >
        {columns.map((c) => {
          const isSorted = sortKey === c.key;
          if (c.sortable) {
            return (
              <button
                key={c.key}
                type="button"
                className={cn(
                  "flex items-center gap-1.5 border-0 bg-transparent p-0 font-mono text-[10px] uppercase tracking-[0.1em]",
                  c.align === "right" && "justify-end",
                  "cursor-pointer select-none hover:text-fg",
                  isSorted ? "text-violet" : "text-fg-mute",
                )}
                onClick={() => toggleSort(c.key)}
              >
                {c.label}
                <span className="text-[8px]">
                  {isSorted ? (sortDir === "asc" ? "▲" : "▼") : "▾"}
                </span>
              </button>
            );
          }
          return (
            <div
              key={c.key}
              className={cn(
                "flex items-center gap-1.5",
                c.align === "right" && "justify-end",
              )}
            >
              {c.label}
            </div>
          );
        })}
      </div>

      {/* Rows */}
      <div ref={animatedRowsRef}>
        {visible.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-fg-mute">
            {emptyMessage}
          </div>
        ) : (
          visible.map((row) => (
            <div
              key={rowKey(row)}
              className="grid items-center gap-3 border-t border-line/60 px-4 py-3 text-sm transition-colors first:border-t-0 hover:bg-surface-2/40"
              style={{ gridTemplateColumns: colTemplate }}
            >
              {columns.map((c) => (
                <div
                  key={c.key}
                  className={cn("min-w-0", c.align === "right" && "text-right")}
                >
                  {c.render(row)}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {sorted.length > pageSize ? (
        <div
          data-testid="data-table-pagination"
          className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-xs text-fg-mute"
        >
          <div>
            {start + 1}-{Math.min(start + pageSize, sorted.length)} of{" "}
            {sorted.length.toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              aria-label="Rows per page"
              value={pageSize}
              onChange={(e) => {
                setTable({ pageSize: Number(e.target.value), page: 1 });
              }}
              className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span className="ml-3 font-mono">
              {safePage} / {totalPages}
            </span>
            <PageBtn onClick={() => setTable({ page: 1 })} disabled={safePage === 1}>
              <ChevronsLeft size={13} />
            </PageBtn>
            <PageBtn
              onClick={() => setTable({ page: safePage - 1 })}
              disabled={safePage === 1}
            >
              <ChevronLeft size={13} />
            </PageBtn>
            <PageBtn
              onClick={() => setTable({ page: safePage + 1 })}
              disabled={safePage === totalPages}
            >
              <ChevronRight size={13} />
            </PageBtn>
            <PageBtn
              onClick={() => setTable({ page: totalPages })}
              disabled={safePage === totalPages}
            >
              <ChevronsRight size={13} />
            </PageBtn>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Small pagination icon button — kept inline since it's only used here. */
function PageBtn({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-surface text-fg-dim hover:bg-surface-2 hover:text-fg disabled:opacity-40"
    >
      {children}
    </button>
  );
}
